// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Shubham Dixit <shubham@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CheckCircle2, ShieldCheck } from 'lucide-react'
import { Logo } from '@/components/Logo'
import { Branding } from '@qh/design-tokens/branding'
import { setKioskVoiceMode } from '@/lib/kiosk-mode'

// Abbreviated consent for in-clinic kiosk flow. Consent is required by HIPAA §3.5
// even though the provider has mediated the handoff verbally. Single-tap confirmation
// (no individual checkboxes) matches the wireframe §8 spec.

export function KioskConsentScreen() {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const items = [
    { title: t('kioskConsent.itemHipaaTitle'),  desc: t('consent.item_hipaa_desc') },
    { title: t('kioskConsent.itemHealthTitle'), desc: t('consent.item_health_desc') },
    { title: t('kioskConsent.itemAiTitle'),     desc: t('consent.item_ai_desc') },
    { title: t('consent.item_comms_title'),  desc: t('consent.item_comms_desc') },
  ]

  return (
    <div className="min-h-dvh flex flex-col bg-gradient-to-br from-[#011829] via-[#012540] to-[#011829] relative overflow-hidden">
      {/* Ambient glow */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-24 left-[10%] w-[500px] h-[500px] rounded-full bg-[#006EF9]/10 blur-[140px]" />
        <div className="absolute bottom-[5%] right-[-5%] w-[350px] h-[350px] rounded-full bg-[#02C7C3]/8 blur-[120px]" />
      </div>

      {/* Top bar */}
      <div className="relative z-10 flex items-center justify-between px-6 pt-10 pb-2 sm:px-10">
        <div style={{ filter: 'brightness(0) invert(1)' }}>
          <Logo variant="mark" className="h-7 w-auto" aria-hidden />
        </div>
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#02C7C3]/15 border border-[#02C7C3]/30">
          <ShieldCheck className="h-3 w-3 text-[#02C7C3]" />
          <span className="text-[11px] font-semibold text-[#02C7C3] uppercase tracking-widest">
            {t('kioskConsent.badge')}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col flex-1 items-center justify-center px-6 py-8 sm:px-10">
        <div className="w-full max-w-sm">
          <h1 className="text-[30px] font-bold text-white leading-tight mb-1.5">
            {t('kioskConsent.title')}
          </h1>
          <p className="text-white/55 text-[14px] leading-relaxed mb-6">
            {t('kioskConsent.subtitle')}
          </p>

          {/* Consent items — read-only list, single-tap acceptance */}
          <div className="flex flex-col gap-2.5 mb-8">
            {items.map((item) => (
              <div
                key={item.title}
                className="flex items-start gap-3 bg-white/6 border border-white/10 rounded-2xl px-4 py-3.5"
              >
                <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-white text-[13px] font-semibold leading-snug">{item.title}</p>
                  <p className="text-white/50 text-[12px] leading-relaxed mt-0.5">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Single-tap confirm */}
          <button
            onClick={() => { setKioskVoiceMode(); navigate('/chat', { replace: true }) }}
            className="w-full h-14 rounded-2xl font-bold text-base text-white bg-gradient-to-r from-[#006EF9] to-[#02C7C3] shadow-[0_0_40px_rgba(0,110,249,0.35)] hover:shadow-[0_0_60px_rgba(2,199,195,0.45)] hover:scale-[1.02] active:scale-95 transition-all duration-200 mb-3"
          >
            {t('kioskConsent.agreeButton')}
          </button>
        </div>
      </div>

      <Branding variant="inverse" className="relative z-10 px-6 pb-6" />
    </div>
  )
}
